import { useEffect, useState } from 'react';
import { FileText, Save, Loader2, Plus, Trash2, Eye } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { toast } from '@/components/ui/sonner';
import { adminAPI } from '@/lib/api';

const Field = ({ label, value, onChange, area, rows = 3, hint, testid }) => (
  <div>
    <Label className="font-body text-xs text-foreground/60 font-medium mb-1.5 block">{label}</Label>
    {area ? (
      <Textarea data-testid={testid} value={value || ''} onChange={(e) => onChange(e.target.value)} rows={rows} className="rounded-xl bg-muted border-border font-body text-sm" />
    ) : (
      <Input data-testid={testid} value={value || ''} onChange={(e) => onChange(e.target.value)} className="rounded-xl bg-muted border-border font-body text-sm" />
    )}
    {hint && <p className="font-body text-[11px] text-foreground/40 mt-1">{hint}</p>}
  </div>
);

const Card = ({ title, children }) => (
  <div className="p-6 sm:p-7 rounded-2xl bg-card border border-border space-y-4">
    <h2 className="font-heading text-lg font-medium text-foreground border-b border-border pb-3">{title}</h2>
    {children}
  </div>
);

export default function AdminAbout() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminAPI.getAbout().then(res => setForm(res.data)).catch(() => toast.error('Failed to load content'));
  }, []);

  const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));
  const setList = (k, i, field, v) => setForm(prev => {
    const arr = [...(prev[k] || [])];
    arr[i] = { ...arr[i], [field]: v };
    return { ...prev, [k]: arr };
  });
  const addItem = (k, item) => setForm(prev => ({ ...prev, [k]: [...(prev[k] || []), item] }));
  const removeItem = (k, i) => setForm(prev => ({ ...prev, [k]: (prev[k] || []).filter((_, idx) => idx !== i) }));

  const save = async () => {
    setSaving(true);
    try {
      const res = await adminAPI.updateAbout(form);
      setForm(res.data);
      toast.success('About page updated');
    } catch {
      toast.error('Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (!form) return (
    <div className="flex items-center justify-center py-24"><Loader2 className="h-6 w-6 animate-spin text-teal" /></div>
  );

  return (
    <div data-testid="admin-about-page" className="max-w-4xl space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="p-2 rounded-xl bg-teal/10"><FileText className="h-5 w-5 text-teal" /></div>
          <div>
            <h1 className="font-heading text-2xl font-medium text-foreground">About Page Content</h1>
            <p className="font-body text-xs text-foreground/60">Edit the public About Us page sections, stat cards and collection points.</p>
          </div>
        </div>
        <div className="flex gap-2">
          <Link to="/about" target="_blank">
            <Button variant="outline" className="rounded-full font-body text-sm gap-2" data-testid="about-preview-btn">
              <Eye className="h-4 w-4" /> Preview
            </Button>
          </Link>
          <Button onClick={save} disabled={saving} data-testid="about-save-btn" className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm font-bold gap-2">
            {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
          </Button>
        </div>
      </div>

      <Card title="Hero Section">
        <Field label="Eyebrow / tagline" testid="about-hero_eyebrow" value={form.hero_eyebrow} onChange={(v) => set('hero_eyebrow', v)} />
        <div className="grid grid-cols-2 gap-4">
          <Field label="Title" testid="about-hero_title" value={form.hero_title} onChange={(v) => set('hero_title', v)} />
          <Field label="Title accent (gold)" testid="about-hero_title_accent" value={form.hero_title_accent} onChange={(v) => set('hero_title_accent', v)} />
        </div>
        <Field label="Welcome paragraph" testid="about-hero_welcome" area rows={4} value={form.hero_welcome} onChange={(v) => set('hero_welcome', v)} />
      </Card>

      <Card title="Our Story">
        <Field label="Eyebrow" value={form.story_eyebrow} onChange={(v) => set('story_eyebrow', v)} />
        <Field label="Heading" value={form.story_heading} onChange={(v) => set('story_heading', v)} />
        <Field label="Body" testid="about-story_body" area rows={6} value={form.story_body} onChange={(v) => set('story_body', v)} hint="Separate paragraphs with a blank line." />
        <Field label="Quote card" area rows={3} value={form.story_quote} onChange={(v) => set('story_quote', v)} />
      </Card>

      <Card title="How It Works">
        <Field label="Eyebrow" value={form.how_eyebrow} onChange={(v) => set('how_eyebrow', v)} />
        <Field label="Heading" value={form.how_heading} onChange={(v) => set('how_heading', v)} />
        <Field label="Intro" area value={form.how_intro} onChange={(v) => set('how_intro', v)} />
        <div className="space-y-3">
          {(form.how_steps || []).map((s, i) => (
            <div key={i} className="rounded-xl border border-border/60 bg-muted/30 p-4 space-y-2" data-testid={`about-step-editor-${i}`}>
              <div className="flex items-center justify-between">
                <span className="font-body text-xs font-semibold text-teal">Step {i + 1}</span>
                <button onClick={() => removeItem('how_steps', i)} className="text-red-400 hover:text-red-300"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
              <Field label="Title" value={s.title} onChange={(v) => setList('how_steps', i, 'title', v)} />
              <Field label="Body" area rows={2} value={s.body} onChange={(v) => setList('how_steps', i, 'body', v)} />
            </div>
          ))}
          <Button variant="outline" onClick={() => addItem('how_steps', { title: '', body: '' })} className="rounded-full font-body text-xs gap-1.5"><Plus className="h-3.5 w-3.5" /> Add step</Button>
        </div>
      </Card>

      <Card title="Pricing & Stat Cards">
        <Field label="Eyebrow" value={form.fees_eyebrow} onChange={(v) => set('fees_eyebrow', v)} />
        <Field label="Heading" value={form.fees_heading} onChange={(v) => set('fees_heading', v)} />
        <Field label="Intro" area value={form.fees_intro} onChange={(v) => set('fees_intro', v)} />
        <div className="space-y-3">
          {(form.fees_cards || []).map((card, i) => (
            <div key={i} className="rounded-xl border border-border/60 bg-muted/30 p-4 space-y-2" data-testid={`about-fee-editor-${i}`}>
              <div className="flex items-center justify-between">
                <span className="font-body text-xs font-semibold text-gold">Card {i + 1}</span>
                <button onClick={() => removeItem('fees_cards', i)} className="text-red-400 hover:text-red-300"><Trash2 className="h-3.5 w-3.5" /></button>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <Field label="Value (e.g. 8%)" value={card.value} onChange={(v) => setList('fees_cards', i, 'value', v)} />
                <Field label="Label" value={card.label} onChange={(v) => setList('fees_cards', i, 'label', v)} />
              </div>
              <Field label="Note" area rows={2} value={card.note} onChange={(v) => setList('fees_cards', i, 'note', v)} />
            </div>
          ))}
          <Button variant="outline" onClick={() => addItem('fees_cards', { value: '', label: '', note: '' })} className="rounded-full font-body text-xs gap-1.5"><Plus className="h-3.5 w-3.5" /> Add card</Button>
        </div>
      </Card>

      <Card title="Collection Points">
        <Field label="Eyebrow" value={form.collection_eyebrow} onChange={(v) => set('collection_eyebrow', v)} />
        <Field label="Heading" value={form.collection_heading} onChange={(v) => set('collection_heading', v)} />
        <Field label="Intro" area value={form.collection_intro} onChange={(v) => set('collection_intro', v)} />
        <Field label="Note (collection / privacy)" area value={form.collection_note} onChange={(v) => set('collection_note', v)} hint="Addresses & host notes are intentionally hidden from the public page." />
        <div className="space-y-2">
          {(form.collection_points || []).map((p, i) => (
            <div key={i} className="flex items-center gap-2" data-testid={`about-cp-editor-${i}`}>
              <Input value={p.location || ''} onChange={(e) => setList('collection_points', i, 'location', e.target.value)} placeholder="Location" className="rounded-xl bg-muted border-border font-body text-sm flex-1" />
              <Input value={p.fee || ''} onChange={(e) => setList('collection_points', i, 'fee', e.target.value)} placeholder="Fee (e.g. R10 / Free)" className="rounded-xl bg-muted border-border font-body text-sm w-40" />
              <button onClick={() => removeItem('collection_points', i)} className="text-red-400 hover:text-red-300 p-2"><Trash2 className="h-4 w-4" /></button>
            </div>
          ))}
          <Button variant="outline" onClick={() => addItem('collection_points', { location: '', fee: '' })} className="rounded-full font-body text-xs gap-1.5"><Plus className="h-3.5 w-3.5" /> Add collection point</Button>
        </div>
      </Card>

      <Card title="Closing Call-to-Action">
        <Field label="Heading" value={form.cta_heading} onChange={(v) => set('cta_heading', v)} />
        <Field label="Body" area value={form.cta_body} onChange={(v) => set('cta_body', v)} />
      </Card>

      <div className="flex justify-end pb-8">
        <Button onClick={save} disabled={saving} className="rounded-full bg-[#C99A4E] border border-transparent text-charcoal hover:bg-gold-light font-body text-sm font-bold gap-2">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Save Changes
        </Button>
      </div>
    </div>
  );
}
